import request from '@/utils/request'
import { getAuthCache } from '@/utils/auth'
import { TOKEN_KEY } from '@/enums/cache'
import { isDev } from '@/utils'
import type {
  AiChatRequest,
  AiChatStreamHandlers,
  AiConversationDeleteRequest,
  AiConversationListRequest,
  AiConversationMessagesOutDto,
  AiConversationMessagesRequest,
  AiConversationResolveRequest,
  AiPageOutDto
} from './model.d'

/** AI 解决状态：0未选 1已解决 2未解决 */
export const AI_RESOLVE_STATUS = {
  NONE: 0,
  SOLVED: 1,
  UNSOLVED: 2
} as const

const AI_API_VERSION = 'scw3180'
const AI_API_PREFIX = 'scw-leyo'

function getLeyoApiBaseUrl() {
  if (isDev()) {
    return 'https://pms.leyodev.cn'
  }
  if (import.meta.env.VITE_BUILD_ENV === 'canary') {
    return 'https://pms2.leyopharm.com'
  }
  return 'https://pms.leyopharm.com'
}

function getAiRequestOptions() {
  return { apiUrl: AI_API_PREFIX }
}

/** AI 会话列表 */
export function listAiConversations(data: AiConversationListRequest = {}) {
  return request.post<AiPageOutDto>(
    {
      url: `/web/ai/conversation/list/${AI_API_VERSION}`,
      data
    },
    getAiRequestOptions()
  )
}

/** AI 会话历史消息 */
export function getAiConversationMessages(data: AiConversationMessagesRequest) {
  return request.post<AiConversationMessagesOutDto>(
    {
      url: `/web/ai/conversation/messages/${AI_API_VERSION}`,
      data
    },
    getAiRequestOptions()
  )
}

/** AI 删除会话 */
export function deleteAiConversation(data: AiConversationDeleteRequest) {
  return request.post<void>(
    {
      url: `/web/ai/conversation/delete/${AI_API_VERSION}`,
      data
    },
    getAiRequestOptions()
  )
}

/** AI 提交解决状态 */
export function resolveAiConversation(data: AiConversationResolveRequest) {
  return request.post<void>(
    {
      url: `/web/ai/conversation/resolve/${AI_API_VERSION}`,
      data
    },
    getAiRequestOptions()
  )
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function pickConversationId(json: Record<string, unknown>): string | undefined {
  const direct =
    (json.ConversationId as string | undefined) || (json.conversationId as string | undefined)
  if (direct) return direct

  const nestedKeys = ['RequestAck', 'Response', 'requestAck', 'response']
  for (const key of nestedKeys) {
    const nested = asRecord(json[key])
    if (!nested) continue
    const id =
      (nested.ConversationId as string | undefined) ||
      (nested.conversationId as string | undefined)
    if (id) return id
  }
  return undefined
}

/**
 * 解析 solar-ai SSE 帧。
 * 文本增量：Type=text.delta，字段 Text
 * 会话 ID：嵌套在 RequestAck / Response.ConversationId
 */
function parseSsePayload(raw: string): {
  conversationId?: string
  content?: string
  errorMessage?: string
  done?: boolean
} {
  const text = raw.trim()
  if (!text) return {}
  if (text === '[DONE]') return { done: true }

  try {
    const json = JSON.parse(text) as Record<string, unknown>
    const type = String(json.Type || json.type || '')
    const conversationId = pickConversationId(json)

    if (type === 'text.delta') {
      const delta =
        (typeof json.Text === 'string' && json.Text) ||
        (typeof json.text === 'string' && json.text) ||
        ''
      return { conversationId, content: delta || undefined }
    }

    // BFF ai-error：{"message":"..."}
    if (typeof json.message === 'string' && !type) {
      return { conversationId, errorMessage: json.message }
    }

    // 兼容旧/简化协议：顶层 content/text 字符串
    if (!type) {
      const contentCandidate = json.content ?? json.delta ?? json.text ?? json.Text
      const content = typeof contentCandidate === 'string' ? contentCandidate : undefined
      return { conversationId, content }
    }

    // 非文本增量帧只提取 conversationId，忽略 StatusDesc 等过程信息
    return { conversationId }
  } catch {
    // 非 JSON 的纯文本片段
    return { content: text }
  }
}

function dispatchSseDataLine(dataLine: string, handlers: AiChatStreamHandlers) {
  const parsed = parseSsePayload(dataLine)
  if (parsed.done) return
  if (parsed.conversationId) {
    handlers.onConversationId?.(parsed.conversationId)
  }
  if (parsed.content) {
    handlers.onContent?.(parsed.content)
  }
}

/**
 * AI 流式聊天（fetch + SSE）
 * EventSource 不支持 POST body，因此用 fetch 读取 text/event-stream
 */
export function chatAiStream(data: AiChatRequest, handlers: AiChatStreamHandlers = {}) {
  const controller = new AbortController()
  const url = `${getLeyoApiBaseUrl()}/${AI_API_PREFIX}/web/ai/chat/${AI_API_VERSION}`

  const run = async () => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          Accept: 'text/event-stream',
          token: String(getAuthCache(TOKEN_KEY) || '')
        },
        body: JSON.stringify(data),
        signal: controller.signal
      })

      if (!response.ok) {
        handlers.onError?.(`请求失败(${response.status})`)
        handlers.onDone?.()
        return
      }

      if (!response.body) {
        handlers.onError?.('响应为空')
        handlers.onDone?.()
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      const consumeSseChunk = (chunk: string) => {
        const lines = chunk.split(/\r?\n/)
        let dataLine = ''
        let eventName = 'message'

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            dataLine += (dataLine ? '\n' : '') + line.slice(5).trimStart()
          }
        }

        if (!dataLine) return

        if (eventName === 'ai-error') {
          const parsed = parseSsePayload(dataLine)
          handlers.onError?.(parsed.errorMessage || parsed.content || 'AI服务异常')
          return
        }

        dispatchSseDataLine(dataLine, handlers)
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const parts = buffer.split(/\r?\n\r?\n/)
        buffer = parts.pop() || ''

        for (const part of parts) {
          consumeSseChunk(part)
        }
      }

      // 冲刷尾包（无结尾空行时）
      const tail = (buffer + decoder.decode()).trim()
      if (tail) {
        consumeSseChunk(tail)
      }

      handlers.onDone?.()
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        handlers.onDone?.()
        return
      }
      handlers.onError?.((error as Error)?.message || '网络异常')
      handlers.onDone?.()
    }
  }

  void run()
  return controller
}
